import { useMemo } from 'react';

/**
 * Trend glyph for table rows: de-emphasised 1.5px line with the current value as an accent end-dot.
 * It is a step series (a price holds until the next print), drawn as such. Values are read from the adjacent
 * price and 24h columns, so the sparkline carries shape only and needs no axis, tooltip or legend.
 */
export function Sparkline({ points, width = 112, height = 32, label }: { points: [number, number][]; width?: number; height?: number; label: string }) {
  const path = useMemo(() => {
    if (points.length < 2) return null;
    const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
    const x0 = Math.min(...xs), x1 = Math.max(...xs);
    let y0 = Math.min(...ys), y1 = Math.max(...ys);
    if (y1 - y0 < y1 * 0.002) { const mid = (y0 + y1) / 2; y0 = mid * 0.999; y1 = mid * 1.001; } // flat series sits mid-height
    const pad = 5;
    const sx = (x: number) => pad + ((x - x0) / Math.max(1, x1 - x0)) * (width - pad * 2);
    const sy = (y: number) => height - pad - ((y - y0) / (y1 - y0)) * (height - pad * 2);
    let d = `M${sx(points[0]![0]).toFixed(1)} ${sy(points[0]![1]).toFixed(1)}`;
    for (let i = 1; i < points.length; i++) d += ` H${sx(points[i]![0]).toFixed(1)} V${sy(points[i]![1]).toFixed(1)}`;
    const last = points.at(-1)!;
    return { d, cx: sx(last[0]), cy: sy(last[1]) };
  }, [points, width, height]);

  if (!path) return <span className="sparkline sparkline--empty" style={{ width, height }} aria-hidden="true" />;
  return (
    <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={path.d} fill="none" stroke="var(--viz-deemph)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={path.cx} cy={path.cy} r={4} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />
    </svg>
  );
}
